import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { useContent } from '../../lib/ContentContext';

// reCAPTCHA v3 is invisible (no checkbox) - it loads Google's script keyed to the site's
// configured site key, then generates a fresh token per submission via grecaptcha.execute().
const recaptchaScripts = {};
function loadRecaptchaV3(siteKey) {
  if (!siteKey) return Promise.resolve(false);
  if (window.grecaptcha && typeof window.grecaptcha.execute === 'function') return Promise.resolve(true);
  if (recaptchaScripts[siteKey]) return recaptchaScripts[siteKey];
  recaptchaScripts[siteKey] = new Promise((resolve) => {
    const existing = document.querySelector(`script[data-recaptcha-key="${siteKey}"]`);
    const finalize = () => {
      // The <script> "load" event fires even when Google's endpoint responds with an HTTP
      // error body (e.g. an invalid/misconfigured site key), so we must verify grecaptcha
      // actually became available before reporting success.
      window.setTimeout(() => {
        const ok = !!(window.grecaptcha && typeof window.grecaptcha.execute === 'function');
        if (!ok) {
          // eslint-disable-next-line no-console
          console.error(
            '[reCAPTCHA v3] Script loaded but window.grecaptcha.execute is unavailable. ' +
            'This means Google rejected the configured site key (commonly caused by an invalid/' +
            'mistyped key, a key of the wrong type, or the domain not being registered for this key ' +
            'at https://www.google.com/recaptcha/admin). Site key in use:', siteKey
          );
        }
        resolve(ok);
      }, 150);
    };
    if (existing) {
      existing.addEventListener('load', finalize);
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaKey = siteKey;
    script.onload = finalize;
    script.onerror = () => {
      // eslint-disable-next-line no-console
      console.error('[reCAPTCHA v3] Failed to load api.js from Google for site key:', siteKey);
      resolve(false);
    };
    document.head.appendChild(script);
  });
  return recaptchaScripts[siteKey];
}

/**
 * variant="home": Name, Email, Products for (select), Message
 * variant="full": Name, Email, Mobile, Company, Industry, Subject, Message
 */
export const ContactForm = ({ variant = 'home', productOptions = [], industryOptions = [] }) => {
  const { settings } = useContent();
  const [form, setForm] = useState({
    name: '', email: '', product: '', message: '', mobile: '', company: '', industry: '', subject: '', website: '',
  });
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const siteKey = settings?.recaptcha_site_key;
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (siteKey) loadRecaptchaV3(siteKey);
  }, [siteKey]);

  const getRecaptchaToken = useCallback(async () => {
    if (!siteKey) return '';
    const ready = await loadRecaptchaV3(siteKey);
    if (!ready || !window.grecaptcha) return '';
    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(siteKey, { action: 'contact_submit' })
          .then((token) => resolve(token || ''))
          .catch(() => resolve(''));
      });
    });
  }, [siteKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'loading', message: '' });
    try {
      const token = await getRecaptchaToken();
      if (siteKey && !token) {
        setStatus({ state: 'error', message: 'Security verification failed to load. Please refresh and try again.' });
        return;
      }
      const res = await api.post('/contact', {
        ...form,
        recaptcha_token: token,
        recaptcha_action: 'contact_submit',
        source: variant === 'home' ? 'home' : 'contact-page',
      });
      setStatus({ state: 'success', message: res.data.message });
      setForm({ name: '', email: '', product: '', message: '', mobile: '', company: '', industry: '', subject: '', website: '' });
    } catch (err) {
      setStatus({
        state: 'error',
        message: err.response?.data?.detail || 'Something went wrong. Please try again.',
      });
    }
  };

  const inputCls =
    'w-full h-12 rounded-lg bg-white border border-[#E6E8EE] px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#26247B] transition-colors duration-200';

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
      {/* Honeypot - hidden from humans, bots fill it */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set('website')}
        tabIndex="-1"
        autoComplete="off"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input data-testid="contact-form-name-input" required placeholder={variant === 'home' ? 'Name' : 'Name*'} value={form.name} onChange={set('name')} className={inputCls} />
        <input data-testid="contact-form-email-input" required type="email" placeholder={variant === 'home' ? 'Email Address' : 'Email ID*'} value={form.email} onChange={set('email')} className={inputCls} />
      </div>

      {variant === 'full' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <input data-testid="contact-form-mobile-input" placeholder="Mobile Number" value={form.mobile} onChange={set('mobile')} className={inputCls} />
          <input data-testid="contact-form-company-input" required placeholder="Company Name*" value={form.company} onChange={set('company')} className={inputCls} />
        </div>
      )}

      {variant === 'home' ? (
        <select data-testid="contact-form-product-select" value={form.product} onChange={set('product')} className={inputCls} required>
          <option value="">Products for</option>
          {productOptions.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <select data-testid="contact-form-industry-select" required value={form.industry} onChange={set('industry')} className={inputCls}>
            <option value="">Industry*</option>
            {industryOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          <input data-testid="contact-form-subject-input" required placeholder="Subject*" value={form.subject} onChange={set('subject')} className={inputCls} />
        </div>
      )}

      <textarea
        data-testid="contact-form-message-textarea"
        required
        placeholder={variant === 'home' ? 'Message' : 'Message*'}
        value={form.message}
        onChange={set('message')}
        rows={6}
        className="w-full rounded-lg bg-white border border-[#E6E8EE] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#26247B] transition-colors duration-200"
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {siteKey ? (
          <p data-testid="contact-form-recaptcha-notice" className="text-xs text-black/50 leading-5 max-w-sm">
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-black/70">Privacy Policy</a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-black/70">Terms of Service</a>{' '}
            apply.
          </p>
        ) : (
          <span />
        )}
        <button
          type="submit"
          data-testid="contact-form-submit-button"
          disabled={status.state === 'loading'}
          className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-[#F4A300] text-[#F4A300] bg-white px-10 py-3 font-extrabold transition-colors duration-200 hover:bg-[#F4A300] hover:text-white disabled:opacity-60 shrink-0"
        >
          {status.state === 'loading' && <Loader2 className="h-4 w-4 animate-spin" />}
          Submit
        </button>
      </div>

      {status.state === 'success' && (
        <div data-testid="contact-form-success-message" className="flex items-start gap-2 rounded-lg bg-[#2E8B57]/10 border border-[#2E8B57] px-4 py-3 text-[#2E8B57] text-sm font-semibold">
          <CheckCircle2 className="h-5 w-5 shrink-0" /> {status.message}
        </div>
      )}
      {status.state === 'error' && (
        <div data-testid="contact-form-error-message" className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-400 px-4 py-3 text-red-700 text-sm font-semibold">
          <AlertCircle className="h-5 w-5 shrink-0" /> {status.message}
        </div>
      )}
    </form>
  );
};
